import { SearchOutlined } from "@ant-design/icons";
import {
  Avatar,
  Button,
  Checkbox,
  Dropdown,
  Form,
  Input,
  List,
  Select,
  Space,
  Switch,
} from "antd";
import { useEffect, useState } from "react";
import {
  Link,
  matchPath,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useAppSelector } from "../../../../app/store";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import userImage from "../../../../assets/images/empty_user.jpg";
import CardTitle from "../../../../components/common/CardTitle";
import CustomCard from "../../../../components/common/CustomCard";
import {
  useAddApprovalMatrixMutation,
  useGetApprovalMatrixDetailsQuery,
  useGetApprovalMatrixListQuery,
  useUpdateApprovalMatrixMutation,
} from "../../../../features/approvalMatrix/approvalMatrixApi";
import {
  useGetProtalAllDepartmentQuery,
  useGetProtalAllSubDepartmentQuery,
} from "../../../../features/portal/settings/settingsApi";
import { useGetAllUsersQuery } from "../../../../features/portal/user/userApi";
import useError from "../../../../hooks/useError";
import useTable from "../../../../hooks/useTable";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { toast } from "../../../../lib/toast";
import { GetAllUsersDataParamsType } from "../../../../types/portal/user";
import { selectArrayOption } from "../../../../utils/utils";

const backLink = "/admin/approval-matrix/vendor-management/department";

export default function ManageDepartmentMatrix() {
  const { id } = useParams();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const { keyword, filter, handleFilter, handleSearchEnter } =
    useTable<GetAllUsersDataParamsType>();

  const location = useLocation();
  const isViewMode = matchPath(
    "/admin/approval-matrix/vendor-management/department/view/:id",
    location.pathname,
  );

  // Keep baseline data
  const [baselineApprovers, setBaselineApprovers] = useState<
    Record<number, { id: number; status: string }>
  >({});
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [open, setOpen] = useState(false);

  // Watch the current approvers in the form
  const currentSelected = Form.useWatch("approvers", form) || {};

  const { data: userData } = useGetAllUsersQuery({
    params: {
      skip: 0,
      page: 1,
      ...filter,
      search: keyword || undefined,
      company_id: user?.data?.company_id,
      limit: 100000,
    },
  });
  const { data: departmentData } = useGetProtalAllDepartmentQuery({
    params: {
      skip: undefined,
      page: 1,
      company_id: user?.data?.company_id,
    },
  });

  const { data: subDepartmentsData } = useGetProtalAllSubDepartmentQuery(
    {
      params: {
        skip: undefined,
        page: 1,
        search: keyword,
        company_id: user?.data?.company_id,
        department_id: filter.department_id,
      },
    },
    { skip: !filter.department_id },
  );

  const [
    createMatrix,
    {
      isLoading: creating,
      isSuccess: created,
      isError: isCreateError,
      error: createError,
    },
  ] = useAddApprovalMatrixMutation();

  const [
    updateMatrix,
    {
      isLoading: updating,
      isSuccess: updated,
      isError: isUpdateError,
      error: updateError,
    },
  ] = useUpdateApprovalMatrixMutation();

  const { data: deptMatrixData } = useGetApprovalMatrixListQuery({
    params: {
      page: 1,
      limit: 0,
      level: "DEPARTMENT",
    },
  });

  const createdDeptIds = deptMatrixData?.data?.departmentApprovers?.map(
    (mat) => mat.id,
  );

  const { data: deptMatrix } = useGetApprovalMatrixDetailsQuery(
    {
      id: id as string,
      level: "DEPARTMENT",
    },
    { skip: !id },
  );

  // Toggle checkbox selection in dropdown
  const toggleSelect = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  // Update active/inactive in form state
  const handleSwitch = (userId: number, value: boolean) => {
    const updatedApprovers = {
      ...currentSelected,
      [userId]: { ...currentSelected[userId], active: value },
    };
    form.setFieldValue("approvers", updatedApprovers);
  };

  // Add selected users to form, only necessary fields
  const onAddApprovers = () => {
    const newApprovers = selectedIds.reduce((acc, id) => {
      const user = userData?.data?.users?.find((u) => u.id === id);
      if (user) {
        acc[id] = {
          id: user.id,
          username: user.username,
          email: user.email,
          active: true,
        };
      }
      return acc;
    }, {} as Record<number, any>);

    form.setFieldValue("approvers", { ...currentSelected, ...newApprovers });
    setSelectedIds([]);
    setOpen(false);
    handleFilter("department_id", undefined);
    handleFilter("sub_department_id", undefined);
    handleSearchEnter(undefined as unknown as string);
  };

  const onFinish = (values: any) => {
    const { department_id, approvers } = values || {};

    // Convert current form approvers
    const current = Object.values(approvers).reduce((acc: any, u: any) => {
      acc[u.id] = { id: u.id, status: u.active ? "active" : "inactive" };
      return acc;
    }, {});

    // Find only changed approvers
    const changed = Object.values(current as any).filter((cur: any) => {
      const base = baselineApprovers[cur.id];
      return !base || base.status !== cur.status; // new user or status changed
    });

    if (!changed.length) {
      toast("info", "No changes detected.");
      return;
    }

    if (id) {
      updateMatrix({
        id,
        body: {
          department_id,
          userInfo: changed,
          level: "DEPARTMENT",
        },
      });
    } else {
      createMatrix({
        department_id,
        userInfo: changed,
        level: "DEPARTMENT",
      });
    }
  };

  useEffect(() => {
    if (created) {
      toast("success", "Matrix successfully created.");
      navigate("/admin/approval-matrix/vendor-management/department");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [created]);

  useEffect(() => {
    if (updated) {
      toast("success", "Matrix successfully updated.");
      navigate("/admin/approval-matrix/vendor-management/department");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updated]);

  useEffect(() => {
    if (deptMatrix?.data?.approval_matrices) {
      const mapped = deptMatrix.data.approval_matrices.reduce(
        (acc: any, item: any) => {
          acc[item.user.id] = {
            id: item.user.id,
            status: item.status, // "active"/"inactive"
          };
          return acc;
        },
        {},
      );
      setBaselineApprovers(mapped);

      // pre-fill form
      const users = deptMatrix.data.approval_matrices.map((item: any) => ({
        id: item.user.id,
        username: item.user.username,
        email: item.user.email,
        active: item.status === "active",
      }));
      form.setFieldsValue({
        department_id: deptMatrix.data.id,
        approvers: users.reduce((acc: any, u: any) => {
          acc[u.id] = u;
          return acc;
        }, {}),
      });
    }
  }, [deptMatrix, form]);

  // Errors
  useError(isCreateError, createError, "Matrix create failed.");
  useError(isUpdateError, updateError, "Matrix update failed.");

  return (
    <SidebarLayout
      title={id ? (isViewMode ? "View Matrix" : "Update Matrix") : "Add Matrix"}
      backLink={backLink}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        className="custom-form"
      >
        <CustomCard className="p-6 pb-0 rounded-b-none">
          <CardTitle>Approval Matrix</CardTitle>

          {/* Department & Approver */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4 mb-5">
            <Form.Item
              label="Department"
              name="department_id"
              rules={[{ required: true, message: "Please enter a department" }]}
            >
              <Select
                placeholder="Select department"
                showSearch
                options={selectArrayOption(
                  id
                    ? departmentData?.data.departments || []
                    : departmentData?.data.departments?.filter(
                        (dept) => !createdDeptIds?.includes(dept.id),
                      ) || [],
                  "name",
                  "id",
                )}
                className="w-full"
                suffixIcon={<SelectDownIcon />}
                allowClear
                optionFilterProp="label"
                disabled={!!id}
              />
            </Form.Item>

            <Form.Item
              label="Approver"
              name="approvers"
              rules={[{ required: true, message: "Please enter an approver" }]}
            >
              <Dropdown
                disabled={!!isViewMode}
                open={open}
                onOpenChange={(flag) => setOpen(flag)}
                trigger={["click"]}
                dropdownRender={() => (
                  <div className="w-full rounded-xl bg-white shadow p-3 space-y-3">
                    <Input
                      prefix={<SearchOutlined />}
                      onChange={(e) => handleSearchEnter(e.target.value)}
                      size="middle"
                      placeholder="Search by Name, Id"
                      className="rounded-md"
                    />
                    <div className="flex items-center justify-between gap-5">
                      <Select
                        allowClear
                        placeholder="Department"
                        value={filter.department_id}
                        onChange={(value) =>
                          handleFilter("department_id", value)
                        }
                        options={selectArrayOption(
                          departmentData?.data.departments || [],
                          "name",
                          "id",
                        )}
                      />
                      <Select
                        allowClear
                        placeholder="Sub Department"
                        value={filter.sub_department_id}
                        disabled={!filter.department_id}
                        onChange={(value) =>
                          handleFilter("sub_department_id", value)
                        }
                        options={selectArrayOption(
                          subDepartmentsData?.data.data || [],
                          "name",
                          "id",
                        )}
                      />
                    </div>
                    <List
                      dataSource={userData?.data?.users?.filter(
                        (u) => !currentSelected[u.id],
                      )}
                      className="h-[250px] overflow-y-scroll"
                      renderItem={(item) => (
                        <List.Item
                          key={item.id}
                          className="flex items-center gap-2 cursor-pointer !justify-start !py-1"
                          onClick={() => toggleSelect(item.id)}
                        >
                          <Checkbox checked={selectedIds.includes(item.id)} />
                          <Avatar src={item.photo || userImage} size={40} />
                          <div className="flex flex-col">
                            <span className="font-medium">{item.username}</span>
                            <span className="text-xs text-gray-500">
                              ID: {item.id}
                            </span>
                          </div>
                        </List.Item>
                      )}
                    />
                    {selectedIds.length > 0 && (
                      <Button
                        onClick={onAddApprovers}
                        size="middle"
                        className="outline-button-primary min-w-[100px] !bg-primary/10 !border-none"
                      >
                        Add
                      </Button>
                    )}
                  </div>
                )}
                placement="bottomLeft"
              >
                <Button className="!border-[#AFBACA] w-full !text-left !pl-3 !text-[#8897AE] flex justify-between items-center disabled:!bg-[#f1f1f1]">
                  Select approver <SelectDownIcon />
                </Button>
              </Dropdown>
            </Form.Item>
          </div>

          {/* Approvers List */}
          <div className="space-y-3 mb-5">
            {Object.values(currentSelected).map((user: any) => (
              <div
                key={user.id}
                className="bg-[#F9FAFB] rounded-lg p-3 flex justify-between items-center"
              >
                <div className="flex items-center gap-3">
                  <img
                    src={user.avatar || userImage}
                    alt={user.username}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <div className="text-slate-700 text-sm font-medium">
                      {user.username}
                    </div>
                    <p className="text-sm text-gray-500 break-all">
                      Email:{" "}
                      <span className="text-blue-500 hover:underline">
                        {user.email}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="w-[105px] flex items-center gap-2">
                  <Switch
                    disabled={!!isViewMode}
                    checked={user.active}
                    onChange={(value) => handleSwitch(user.id, value)}
                  />
                  <span className="text-zinc-700 text-sm font-medium">
                    {user.active ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CustomCard>

        {/* Form Buttons */}
        {!isViewMode && (
          <div className="p-6 rounded-md rounded-t-none bg-white border border-t-0 border-light">
            <Space className="w-full mt-2" size={12}>
              <Link to={backLink}>
                <Button
                  disabled={!!isViewMode}
                  className="normal-dark-button modal-button"
                  size="middle"
                >
                  Cancel
                </Button>
              </Link>
              <Button
                disabled={!!isViewMode}
                className="modal-button"
                type="primary"
                htmlType="submit"
                size="middle"
                loading={creating || updating}
              >
                {id ? "Update Matrix" : "Add Matrix"}
              </Button>
            </Space>
          </div>
        )}
      </Form>
    </SidebarLayout>
  );
}
